import {
  ArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  DateTimeInput,
  FormDataConsumer,
  maxValue,
  minValue,
  NullableBooleanInput,
  NumberInput,
  regex,
  SelectArrayInput,
  SimpleFormIterator,
  required as requiredValidator,
  TextInput,
  useGetList,
  useGetMany,
} from 'react-admin';
import { useTranslate, useWrappedSource } from 'ra-core';
import { useFormContext, useWatch } from 'react-hook-form';
import _ from 'lodash';
import {
  ReactNode, useCallback, useId, useMemo, useState,
} from 'react';
import { Chip } from '@mui/material';
import { PlayArrow } from '@mui/icons-material';
import {
  getFinitePrimitiveUnionChoices,
  getOneOfVariants,
  isDiscriminatedObjectOneOf,
  pickOneOfVariant,
  resolveSchemaForForm,
  STRING_OR_ENUM_ARRAY_UNION,
} from './clientConfigSchemaResolve';
import { getCurrentCustomer } from './currentCustomer';

const indentBorderStyle = '2px solid #e0e0e0';

const translateResource = (resource: string) => {

  // The dashboard resource is camelCase, not the kebab-plural used for other refs.
  if (resource === 'ResponsePartner') {

    return 'responsePartners';

  }
  const splitResource = resource.split(/(?=[A-Z])/);
  const joinedResource = splitResource.join('-');

  return `${joinedResource.toLowerCase()}s`;

};

const CLIENT_CONFIG_REF_SORT = { field: 'name', order: 'ASC' as const };
const CLIENT_CONFIG_REF_PER_PAGE = 100;

// Same resources the data provider lists without a customer filter.
const CUSTOMER_UNSCOPED_REFERENCES = new Set([
  'customers',
  'responsePartners',
  'service-accounts',
  'roles',
  'domain-links',
]);

const recordCustomerId = (record: any): string => {

  const customer = record?.customer;
  if (!customer) {

    return '';

  }
  if (typeof customer === 'string') {

    return customer;

  }
  return String(customer._id ?? customer.id ?? '');

};

const selectedRecordVisibleForCustomer = (record: any, reference: string, customerId: string) => {

  if (CUSTOMER_UNSCOPED_REFERENCES.has(reference)) {

    return true;

  }
  const ownerId = recordCustomerId(record);
  if (!ownerId) {

    return true;

  }
  return ownerId === customerId;

};

const literalRefName = (refSchema: any): string | undefined => {

  if (!refSchema || typeof refSchema !== 'object') {

    return undefined;

  }
  if (typeof refSchema.const === 'string') {

    return refSchema.const;

  }
  if (Array.isArray(refSchema.enum)) {

    const values = refSchema.enum.filter((value: unknown) => typeof value === 'string');
    if (values.length === 1) {

      return values[0];

    }

  }
  return undefined;

};

const schemaAllowsNull = (node: any): boolean => {

  if (!node || typeof node !== 'object') {

    return false;

  }
  if (node.nullable || node.type === 'null') {

    return true;

  }
  if (Array.isArray(node.type) && node.type.includes('null')) {

    return true;

  }
  const variants = node.anyOf ?? node.oneOf;
  return Array.isArray(variants) && variants.some((variant: any) => (
    (resolveSchemaForForm(variant) ?? variant)?.type === 'null'
  ));

};

/** A `{ _id, _ref }` object, an array of them, or the same wrapped in a null union. */
const describeClientConfigRefField = (
  schema: any,
  depth = 0,
): { refType: string; multiple: boolean } | undefined => {

  if (!schema || typeof schema !== 'object' || depth > 5) {

    return undefined;

  }
  const resolved = resolveSchemaForForm(schema) ?? schema;
  const objectRef = literalRefName(resolved.properties?._ref);
  if (objectRef) {

    return { refType: objectRef, multiple: false };

  }
  const types = Array.isArray(resolved.type) ? resolved.type : [resolved.type];
  if (resolved.items && types.includes('array')) {

    const itemField = describeClientConfigRefField(resolved.items, depth + 1);
    if (itemField && !itemField.multiple) {

      return { refType: itemField.refType, multiple: true };

    }

  }
  const variants = resolved.anyOf ?? resolved.oneOf;
  if (!Array.isArray(variants)) {

    return undefined;

  }
  const nonNull = variants.filter((variant: any) => (resolveSchemaForForm(variant) ?? variant)?.type !== 'null');
  if (nonNull.length !== 1) {

    return undefined;

  }
  return describeClientConfigRefField(nonNull[0], depth + 1);

};

const refRecordId = (value: unknown): string => {

  if (value == null || value === '') {

    return '';

  }
  if (typeof value === 'object') {

    return String((value as { _id?: unknown })._id ?? '');

  }
  return String(value);

};

const toRefRecord = (id: unknown, refType: string, previous?: unknown) => {

  const extra = previous && typeof previous === 'object' ? previous as Record<string, unknown> : {};
  return { ...extra, _id: id, _ref: refType };

};

const formatRefList = (value: unknown): string[] => {

  if (!Array.isArray(value)) {

    return [];

  }
  return value.map(refRecordId).filter(Boolean);

};

const parseRefList = (value: unknown, refType: string, previous: unknown) => {

  if (!Array.isArray(value)) {

    return [];

  }
  const previousById = new Map<string, unknown>();
  if (Array.isArray(previous)) {

    previous.forEach((item) => {

      const id = refRecordId(item);
      if (id) {

        previousById.set(id, item);

      }

    });

  }
  return value
    .filter((item) => item != null && item !== '')
    .map((item) => (

      typeof item === 'object' ? item : toRefRecord(item, refType, previousById.get(String(item)))

    ));

};

const clientConfigRefOptionText = (record?: { name?: string; id?: string }) => (
  record?.name || String(record?.id ?? '')
);

const matchAnyClientConfigRefChoice = () => true;

const isColor = (strColor: string) => {

  const s = new Option().style;
  s.color = strColor;
  return s.color !== '';

};

/** Extract enum choices from a JSON Schema node (enum, anyOf/oneOf const unions). */
export const getSchemaEnumChoices = (schema: any): unknown[] | undefined => {

  if (!schema) {

    return undefined;

  }
  if (schema.enum) {

    return schema.enum;

  }
  const union = schema.anyOf ?? schema.oneOf;
  if (Array.isArray(union)) {

    const fromConst = union
      .map((entry: any) => entry?.const ?? (entry?.enum?.length === 1 ? entry.enum[0] : undefined))
      .filter((value: unknown) => value !== undefined);
    if (fromConst.length === union.length) {

      return fromConst;

    }

  }
  return undefined;

};

export const getDepth = (key: string, allKeys: string[]) => {

  const currentSplitKey = key.split('.');
  let depth = 0;
  for (let i = currentSplitKey.length - 1; i > 0; i--) {

    const currentKey = currentSplitKey.slice(0, i).join('.');
    const hasSiblings = allKeys.some((innerKey) => innerKey.startsWith(currentKey) && innerKey !== key);
    if (hasSiblings) {

      depth = i;
      break;

    }

  }

  return depth;

};

export const getTitleFromKey = (key: string, index?: number) => {

  const splitKey = key.split('.');
  const title = splitKey[index !== undefined ? index : splitKey.length - 1]?.replace(/([A-Z])/g, ' $1');
  if (!title) {

    return '';

  }
  return title[0].toUpperCase() + title.slice(1).toLowerCase();

};

export type ClientConfigSchemaFieldDefinition = {
  key: string;
  schema?: any;
  editableBy?: ('Customer' | 'AssetGroup' | 'Device')[];
  mergeHistory?: any[];
};

const collectSchemaPropertyPaths = (schema: any, prefix = ''): string[] => {

  const resolved = resolveSchemaForForm(schema) ?? schema;
  if (!resolved?.properties) {

    return prefix ? [prefix] : [];

  }
  const paths: string[] = [];
  for (const key of Object.keys(resolved.properties)) {

    const path = prefix ? `${prefix}.${key}` : key;
    const propSchema = resolveSchemaForForm(resolved.properties[key]) ?? resolved.properties[key];
    if (propSchema?.type === 'object' && propSchema.properties && !propSchema.properties._ref) {

      paths.push(...collectSchemaPropertyPaths(propSchema, path));

    } else {

      paths.push(path);

    }

  }
  return paths;

};

const getCommonPathDepth = (left: string, right: string): number => {

  const leftParts = left.split('.');
  const rightParts = right.split('.');
  const limit = Math.min(leftParts.length, rightParts.length);
  let depth = 0;
  while (depth < limit && leftParts[depth] === rightParts[depth]) {

    depth += 1;

  }
  return depth;

};

const getSectionTitleBetweenPaths = (previousPath: string | undefined, currentPath: string): string => {

  if (!previousPath) {

    const depth = currentPath.split('.').length;
    if (depth > 1) {

      return getTitleFromKey(currentPath, depth - 2);

    }
    return '';

  }

  const previousParts = previousPath.split('.');
  const currentParts = currentPath.split('.');
  const shareParentBranch = previousParts.length > 0
    && currentParts.length > 0
    && previousParts.slice(0, -1).join('.') === currentParts.slice(0, -1).join('.');

  if (shareParentBranch) {

    return '';

  }

  const commonDepth = getCommonPathDepth(previousPath, currentPath);
  if (commonDepth < currentParts.length) {

    return getTitleFromKey(currentParts[commonDepth]);

  }
  return '';

};

export type SchemaSection<T> = {
  title: string | null;
  items: T[];
};

export function groupBySchemaSection<T>(
  items: T[],
  getPropertyKey: (item: T) => string,
  sourcePrefix: string,
): SchemaSection<T>[] {

  const sections: SchemaSection<T>[] = [];
  let current: SchemaSection<T> = { title: null, items: [] };

  items.forEach((item, index) => {

    const key = getPropertyKey(item);
    const source = sourcePrefix ? `${sourcePrefix}.${key}` : key;
    const previousKey = index > 0 ? getPropertyKey(items[index - 1]) : undefined;
    let previousPath: string | undefined;
    if (previousKey) {

      previousPath = sourcePrefix ? `${sourcePrefix}.${previousKey}` : previousKey;

    }
    const sectionTitle = getSectionTitleBetweenPaths(previousPath, source);
    if (sectionTitle) {

      if (current.items.length > 0) {

        sections.push(current);

      }
      current = { title: sectionTitle, items: [item] };

    } else {

      current.items.push(item);

    }

  });
  if (current.items.length > 0) {

    sections.push(current);

  }
  return sections;

}

export const CollapsibleConfigSection = (props: {
  title: string;
  headingLevel?: 'h1' | 'h2';
  defaultCollapsed?: boolean;
  hitCount?: number;
  lazyMount?: boolean;
  children: ReactNode;
}) => {

  const {
    title,
    headingLevel = 'h2',
    defaultCollapsed = true,
    hitCount = 0,
    lazyMount = true,
    children,
  } = props;
  const contentId = useId();
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const [mounted, setMounted] = useState(!lazyMount || !defaultCollapsed);

  const toggle = () => {

    if (collapsed && lazyMount) {

      setMounted(true);

    }
    setCollapsed((value) => !value);

  };
  const HeadingTag = headingLevel;

  return (
    <div style={{ marginTop: '1em' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          marginBottom: '0.5em',
        }}
        onClick={toggle}
        tabIndex={0}
        onKeyDown={(event) => {

          if (event.key === 'Enter' || event.key === ' ') {

            event.preventDefault();
            toggle();

          }

        }}
        aria-expanded={!collapsed}
        aria-controls={contentId}
        role="button"
      >
        <PlayArrow
          sx={{
            transform: collapsed ? 'rotate(0deg)' : 'rotate(90deg)',
            transition: 'transform 0.2s',
            marginRight: '0.5em',
          }}
        />
        <HeadingTag style={{ margin: 0, fontSize: headingLevel === 'h1' ? '20px' : '18px' }}>
          {title}
        </HeadingTag>
        {hitCount > 0 && (
          <Chip
            label={hitCount}
            size="small"
            sx={{ marginLeft: '0.75em', backgroundColor: 'error.main', color: '#fff' }}
          />
        )}
      </div>
      {(mounted || !lazyMount) && (
        <div
          id={contentId}
          style={{ display: collapsed ? 'none' : 'block' }}
        >
          {children}
        </div>
      )}
    </div>
  );

};

function SchemaObjectPropertyFields(props: {
  schema: any;
  sourcePrefix: string;
  editableBy?: ClientConfigSchemaFieldDefinition['editableBy'];
  mergeHistory?: ClientConfigSchemaFieldDefinition['mergeHistory'];
  fallback?: ReactNode;
  lazyMountSections?: boolean;
}) {

  const {
    schema, sourcePrefix, editableBy, mergeHistory, fallback, lazyMountSections = true,
  } = props;
  const resolvedSchema = resolveSchemaForForm(schema) ?? schema;
  if (!resolvedSchema?.properties) {

    return null;

  }

  const propertyKeys = Object.keys(resolvedSchema.properties);
  const allPaths = collectSchemaPropertyPaths(resolvedSchema, sourcePrefix);
  const sections = groupBySchemaSection(propertyKeys, (key) => key, sourcePrefix);

  const renderPropertyField = (key: string) => {

    const source = sourcePrefix ? `${sourcePrefix}.${key}` : key;
    const isPropertyRequired = resolvedSchema.required?.includes(key);
    const calcDepth = getDepth(source, allPaths);
    return (
      <div
        style={{
          paddingLeft: `${calcDepth}em`,
          borderLeft: calcDepth > 0 ? indentBorderStyle : 'none',
          order: calcDepth,
        }}
        className={`margin-${calcDepth}`}
        key={key}
      >
        {/* eslint-disable-next-line @typescript-eslint/no-use-before-define -- mutual recursion with ConfigFieldByType */}
        <ConfigFieldByType definition={{
          schema: {
            ...resolvedSchema.properties[key],
            nullable: !isPropertyRequired,
          },
          key: source,
          editableBy: resolvedSchema.properties[key].editableBy ?? editableBy,
          mergeHistory: schema.mergeHistory || mergeHistory,
        }}
        fallback={fallback}
        />
      </div>
    );

  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {sections.map((section) => {

        if (!section.title) {

          return section.items.map((key) => renderPropertyField(key));

        }
        const sectionId = `${sourcePrefix}::${section.title}`;
        return (
          <CollapsibleConfigSection
            key={sectionId}
            title={section.title}
            lazyMount={lazyMountSections}
          >
            {section.items.map((key) => renderPropertyField(key))}
          </CollapsibleConfigSection>
        );

      })}
    </div>
  );

}

const primitiveChoiceId = (value: unknown): string => `${typeof value}:${String(value)}`;

const PrimitiveUnionField = (props: {
  source: string;
  label: string;
  required: boolean;
  fullWidth: boolean;
  helperText?: string | false;
  size?: 'small' | 'medium';
  sx: any;
  choices: unknown[];
}) => {

  const {
    choices, ...inputProps
  } = props;
  const selectChoices = choices.map((choice) => ({
    id: primitiveChoiceId(choice),
    name: String(choice),
  }));
  const valueById = new Map(choices.map((choice) => [primitiveChoiceId(choice), choice]));

  return (
    <AutocompleteInput
      {...inputProps}
      choices={selectChoices}
      optionText="name"
      optionValue="id"
      translateChoice={false}
      isRequired={inputProps.required}
      format={(value) => {

        if (value === null || value === undefined) {

          return '';

        }
        const id = primitiveChoiceId(value);
        return valueById.has(id) ? id : '';

      }}
      parse={(value) => {

        if (value === '' || value === null || value === undefined) {

          return null;

        }
        return valueById.has(value) ? valueById.get(value) : value;

      }}
    />
  );

};

const StringOrEnumArrayUnionField = (props: {
  source: string;
  schema: any;
  label: string;
  required: boolean;
  sx: any;
}) => {

  const {
    source, schema, label, required, sx,
  } = props;
  const choices = schema.enum ?? schema.items?.enum ?? [];

  return (
    <FormDataConsumer>
      {({ formData }) => {

        const value = _.get(formData, source);
        if (Array.isArray(value)) {

          return (
            <SelectArrayInput
              source={source}
              choices={choices}
              label={label}
              helperText={false}
              size="small"
              fullWidth
              sx={sx}
            />
          );

        }
        return (
          <AutocompleteInput
            source={source}
            choices={choices}
            label={label}
            helperText={false}
            size="small"
            fullWidth
            sx={sx}
            isRequired={required}
          />
        );

      }}
    </FormDataConsumer>
  );

};

const ClientConfigRefInput = (props: {
  source: string;
  label: string;
  required: boolean;
  multiple: boolean;
  refType: string;
  helperText?: string | false;
  sx: any;
  size?: 'small' | 'medium';
  fullWidth: boolean;
}) => {

  const {
    source, label, required, multiple, refType, helperText, sx, size, fullWidth,
  } = props;
  const finalSource = useWrappedSource(source);
  const { getValues } = useFormContext();
  const currentValue = useWatch({ name: finalSource });
  const reference = translateResource(refType);
  const customerId = getCurrentCustomer()?.value ?? '';
  const [search, setSearch] = useState('');
  const setFilter = useCallback((value: string) => {

    setSearch(value);

  }, []);
  const customerScoped = !CUSTOMER_UNSCOPED_REFERENCES.has(reference);
  const listFilter = useMemo(() => ({
    ...(customerId && customerScoped ? { 'customer._id': customerId } : {}),
    ...(search ? { q: search } : {}),
  }), [customerId, customerScoped, search]);
  const selectedIds = useMemo(() => (

    multiple ? formatRefList(currentValue) : [refRecordId(currentValue)].filter(Boolean)

  ), [currentValue, multiple]);
  const { data: listRecords = [], isPending, isFetching } = useGetList(reference, {
    pagination: { page: 1, perPage: CLIENT_CONFIG_REF_PER_PAGE },
    sort: CLIENT_CONFIG_REF_SORT,
    filter: listFilter,
  }, { enabled: Boolean(customerId) });
  const { data: selectedRecords = [] } = useGetMany(reference, {
    ids: selectedIds,
  }, { enabled: selectedIds.length > 0 });
  const choices = useMemo(() => {

    const byId = new Map<string, any>();
    listRecords.forEach((record) => {

      if (record?.id != null) {

        byId.set(String(record.id), record);

      }

    });
    selectedRecords.forEach((record) => {

      if (record?.id == null || byId.has(String(record.id))) {

        return;

      }
      if (!selectedRecordVisibleForCustomer(record, reference, customerId)) {

        return;

      }
      byId.set(String(record.id), record);

    });
    return Array.from(byId.values());

  }, [customerId, listRecords, reference, selectedRecords]);

  const format = (value: unknown) => (multiple ? formatRefList(value) : refRecordId(value));
  const parse = (value: unknown) => {

    const previous = getValues(finalSource);
    if (multiple) {

      return parseRefList(value, refType, previous);

    }
    if (value == null || value === '') {

      return null;

    }
    if (typeof value === 'object') {

      return value;

    }
    return toRefRecord(value, refType, previous);

  };

  const inputProps = {
    source,
    label,
    helperText,
    sx,
    size,
    fullWidth,
    choices,
    optionText: clientConfigRefOptionText,
    translateChoice: false,
    // Server search supplies the list. Skip client-side filtering so the open dropdown is not limited to the current label.
    matchSuggestion: matchAnyClientConfigRefChoice,
    isRequired: required,
    validate: required ? requiredValidator() : undefined,
    isPending,
    isFetching,
    setFilter,
    format,
    parse,
  };

  if (multiple) {

    return <AutocompleteArrayInput {...inputProps} />;

  }
  return <AutocompleteInput {...inputProps} />;

};

export function ConfigFieldByType(props: {
  definition: ClientConfigSchemaFieldDefinition;
  fallback?: ReactNode;
}) {

  const translate = useTranslate();
  const { definition, fallback } = props;
  if (!definition.schema) {

    if (fallback) {

      return <>{fallback}</>;

    }
    return (
      <div><span>
        unsupported type: unknown
      </span></div>
    );

  }

  const schema = resolveSchemaForForm(definition.schema) ?? definition.schema;

  const isRequired = () => {

    if (schema.nullable) {

      return false;

    }
    return schema['x-optional'] !== true;

  };

  const generalProps: {
    source: string;
    required: boolean;
    fullWidth: boolean;
    helperText?: string | false;
    size?: 'small' | 'medium';
    label: string;
    sx: any;
  } = {
    source: definition.key,
    required: isRequired(),
    fullWidth: true,
    helperText: false,
    size: 'small',
    label: getTitleFromKey(definition.key),
    sx: { marginBottom: '5px', marginTop: '5px' },
  };

  const refField = describeClientConfigRefField(schema);
  if (refField) {

    return (
      <ClientConfigRefInput
        source={generalProps.source}
        label={generalProps.label}
        required={generalProps.required && !schemaAllowsNull(schema)}
        multiple={refField.multiple}
        refType={refField.refType}
        helperText={generalProps.helperText}
        sx={generalProps.sx}
        size={generalProps.size}
        fullWidth={generalProps.fullWidth}
      />
    );

  }

  if (schema[STRING_OR_ENUM_ARRAY_UNION]) {

    return (
      <StringOrEnumArrayUnionField
        source={definition.key}
        schema={schema}
        label={generalProps.label}
        required={generalProps.required}
        sx={generalProps.sx}
      />
    );

  }

  const primitiveUnionChoices = getFinitePrimitiveUnionChoices(schema);
  if (primitiveUnionChoices) {

    return (
      <PrimitiveUnionField
        {...generalProps}
        choices={primitiveUnionChoices}
      />
    );

  }

  if (schema.type === 'string') {

    if (schema.format === 'date-time') {

      return (
        <DateTimeInput {...generalProps} />
      );

    }

    if (schema.enum) {

      return (
        <AutocompleteInput {...generalProps} choices={schema.enum} isRequired={generalProps.required} />
      );

    }

    const mergeHistoryValue = definition.mergeHistory?.[0]?.value;
    if (typeof mergeHistoryValue === 'string' && isColor(mergeHistoryValue)) {

      return (
        <div style={{ display: 'flex', flexDirection: 'row', gap: '0.5em' }}>
          <FormDataConsumer>
            {({ formData }) => {

              const value = _.get(formData, definition.key);
              if (isColor(value)) {

                return (
                  <div className="app-defaults-color-diff" style={{
                    display: 'flex',
                    flexDirection: 'row',
                  }}>
                    <div className={'app-defaults-color-diff-split'} style={{
                      backgroundColor: mergeHistoryValue,
                    }}
                    />
                    <div className={'app-defaults-color-diff-split'} style={{
                      backgroundColor: value,
                    }}/>
                  </div>
                );

              }
              return (
                <div className="app-defaults-color-diff" style={{
                  backgroundColor: mergeHistoryValue,
                }}/>
              );

            }}
          </FormDataConsumer>
          <TextInput {...generalProps} />
        </div>
      );

    }
    return (
      <TextInput
        {...generalProps}
        validate={schema.pattern
          ? regex(new RegExp(schema.pattern), translate('ra.validation.regex', { pattern: schema.pattern }))
          : undefined}
      />
    );

  }
  if (schema.type === 'number' || schema.type === 'integer') {

    const validation = [];
    if (schema.minimum !== undefined) {

      validation.push(minValue(schema.minimum));

    }
    if (schema.maximum !== undefined) {

      validation.push(maxValue(schema.maximum));

    }
    return (
      <NumberInput {...generalProps} min={schema.minimum} max={schema.maximum} validate={validation} parse={(v) => {

        if (v === '' || v === null || v === undefined) {

          return null;

        }
        const parsed = schema.type === 'integer' ? parseInt(v, 10) : parseFloat(v);
        return Number.isNaN(parsed) ? null : parsed;

      }} />
    );

  }
  if (schema.type === 'boolean') {

    return (
      <NullableBooleanInput {...generalProps} />
    );

  }
  if (schema.type === 'object') {

    if (schema?.properties) {

      return (
        <SchemaObjectPropertyFields
          schema={schema}
          sourcePrefix={definition.key}
          editableBy={definition.editableBy}
          mergeHistory={schema.mergeHistory || definition.mergeHistory}
          fallback={fallback}
        />
      );

    }

  }
  if (schema.type === 'array') {

    const rawItemSchema = definition.schema.items ?? {};
    const itemSchema = resolveSchemaForForm(rawItemSchema) ?? rawItemSchema;
    const itemVariants = getOneOfVariants(rawItemSchema);
    const isDiscriminatedItems = itemVariants && isDiscriminatedObjectOneOf(itemVariants);

    const itemEnumChoices = getSchemaEnumChoices(itemSchema);
    if (itemEnumChoices?.length) {

      return (
        <ArrayInput {...generalProps}>
          <SimpleFormIterator
            inline
            getItemLabel={(index) => `#${index + 1}`}
          >
            <AutocompleteInput
              source=""
              choices={itemEnumChoices}
              label={false}
              helperText={false}
              size="small"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-notchedOutline legend': {
                  display: 'none',
                },
                '& .MuiOutlinedInput-notchedOutline': {
                  top: 0,
                },
              }}
            />
          </SimpleFormIterator>
        </ArrayInput>
      );

    }

    const renderArrayItemFields = (activeItemSchema: any) => (
      <SchemaObjectPropertyFields
        schema={activeItemSchema}
        sourcePrefix=""
        editableBy={definition.editableBy}
        mergeHistory={definition.mergeHistory}
        fallback={fallback}
        lazyMountSections={false}
      />
    );

    if (isDiscriminatedItems || itemSchema.properties) {

      const firstVariant = itemVariants?.[0];
      let defaultItemSchema = itemSchema;
      if (!itemSchema.properties && firstVariant) {

        defaultItemSchema = resolveSchemaForForm(firstVariant) ?? firstVariant;

      }

      return (
        <div
          style={{
            paddingLeft: '1em',
            borderLeft: indentBorderStyle,
          }}>
          <h2 style={{ margin: '1em 0 0.5em 0', fontSize: '18px', order: 0 }}>{getTitleFromKey(definition.key)}</h2>
          <ArrayInput source={definition.key} label={false}>
            <SimpleFormIterator>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  backgroundColor: 'white',
                  padding: '1em',
                  border: '1px dashed #ccc',
                }}
              >
                {isDiscriminatedItems && itemVariants ? (
                  <FormDataConsumer>
                    {({ scopedFormData }) => {

                      let variantSchema;
                      for (const discriminator of ['mergeKey', 'type']) {

                        const discValue = scopedFormData?.[discriminator];
                        variantSchema = pickOneOfVariant(itemVariants, discriminator, discValue);
                        if (variantSchema) {

                          break;

                        }

                      }
                      const activeSchema = resolveSchemaForForm(variantSchema ?? defaultItemSchema)
                        ?? variantSchema
                        ?? defaultItemSchema;
                      return renderArrayItemFields(activeSchema);

                    }}
                  </FormDataConsumer>
                ) : (
                  renderArrayItemFields(itemSchema)
                )}
              </div>
            </SimpleFormIterator>
          </ArrayInput>
        </div>
      );

    }
    if (itemSchema.type === 'number' && itemSchema.minimum !== undefined && itemSchema.maximum !== undefined) {

      const options = [];
      for (let i = itemSchema.minimum; i <= itemSchema.maximum; i++) {

        options.push({ id: i, name: i });

      }
      return (
        <SelectArrayInput {...generalProps} source={definition.key} choices={options}/>
      );

    }

    const resolvedItemForFallback = resolveSchemaForForm(rawItemSchema) ?? rawItemSchema;
    return (
      <ArrayInput source={definition.key}>
        <SimpleFormIterator>
          <ConfigFieldByType definition={{
            key: '',
            schema: resolvedItemForFallback,
            editableBy: definition.editableBy,
            mergeHistory: definition.mergeHistory,
          }}
          fallback={fallback}
          />
        </SimpleFormIterator>
      </ArrayInput>
    );

  }

  if (fallback) {

    return <>{fallback}</>;

  }
  return (
    <div><span>
      unsupported type: {schema.type} {definition.key}
    </span></div>
  );

}
